// X (Twitter) Ads website pixel — uet tag.
//
// Only injected after the user grants marketing consent (UK PECR / ePrivacy),
// mirroring the Meta pixel in ./metaPixel.js.
//
// Set the pixel ID via VUE_APP_X_PIXEL_ID. Find it in X Ads Manager under
// Tools -> Events Manager -> your website pixel (the short base tag ID).

import { hasSecretInPath } from './secretRoutes';

const PIXEL_ID = process.env.VUE_APP_X_PIXEL_ID || 're2yn';
const SCRIPT_ID = 'x-pixel';
const SCRIPT_SRC = 'https://static.ads-twitter.com/uwt.js';

// Conversion event IDs from X Events Manager (Tools -> Events Manager).
// 'Book Click (Outbound)' — an Add to cart event, fired when a user clicks
// through to a partner booking site.
const BOOK_CLICK_EVENT_ID =
  process.env.VUE_APP_X_BOOK_CLICK_EVENT_ID || 'tw-re2yn-re33o';

let initialized = false;
let lastPageViewUrl = null;

function createTwq() {
  if (window.twq) return window.twq;

  const twq = function (...args) {
    twq.exe ? twq.exe.apply(twq, args) : twq.queue.push(args);
  };

  twq.version = '1.1';
  twq.queue = [];
  window.twq = twq;

  return twq;
}

export function enableXPixel() {
  if (typeof window === 'undefined' || typeof document === 'undefined') return;
  if (!PIXEL_ID) return;

  const twq = createTwq();

  if (!document.getElementById(SCRIPT_ID)) {
    const script = document.createElement('script');
    script.id = SCRIPT_ID;
    script.async = true;
    script.src = SCRIPT_SRC;
    document.head.appendChild(script);
  }

  if (!initialized) {
    // This call itself reports the first site visit — don't also call
    // trackXPageView() here or the landing page is counted twice.
    if (!hasSecretInPath()) {
      twq('config', PIXEL_ID);
      lastPageViewUrl = window.location.href;
    }
    initialized = true;
  }
}

export function disableXPixel() {
  if (typeof window === 'undefined' || typeof document === 'undefined') return;

  const script = document.getElementById(SCRIPT_ID);
  if (script) script.remove();

  initialized = false;
  lastPageViewUrl = null;
}

export function trackXPageView() {
  if (typeof window === 'undefined' || !window.twq || !initialized) return;
  if (hasSecretInPath()) return;

  const pageUrl = window.location.href;
  if (pageUrl === lastPageViewUrl) return;
  lastPageViewUrl = pageUrl;

  // The base `config` call is what reports a site visit to X. There is no
  // separate page-view event ID, so for SPA route changes we re-issue config
  // against the new URL. The lastPageViewUrl guard prevents double counting
  // when a route resolves to the same URL.
  window.twq('config', PIXEL_ID);
}

/**
 * Report a conversion event to X.
 *
 * @param {string} eventId  Full event ID from Events Manager, e.g. 'tw-re2yn-abcde'
 * @param {object} payload  Optional {value, currency, contents, conversion_id}
 */
export function trackXEvent(eventId, payload = {}) {
  if (typeof window === 'undefined' || !window.twq || !initialized) return;
  if (!eventId) return;
  window.twq('event', eventId, payload);
}

/**
 * Report a book click (outbound to a partner booking site) to X.
 *
 * Silently no-ops when marketing consent has not been granted, so callers can
 * fire this unconditionally.
 *
 * @param {object}        opts
 * @param {number|string} opts.value        Fare shown at click time.
 * @param {string}        opts.currency     ISO currency code, e.g. 'GBP'.
 * @param {string}        opts.conversionId Optional dedupe ID. Generated when omitted.
 */
export function trackXBookClick({ value, currency, conversionId } = {}) {
  if (!BOOK_CLICK_EVENT_ID) return;

  const payload = {};

  const numericValue = Number(value);
  if (value !== null && value !== undefined && value !== '' && !Number.isNaN(numericValue)) {
    payload.value = numericValue;
  }
  if (currency) payload.currency = currency;

  // A per-click ID lets X de-duplicate if we later also report this event
  // server-side via the Conversion API.
  payload.conversion_id = conversionId || generateConversionId();

  trackXEvent(BOOK_CLICK_EVENT_ID, payload);
}

function generateConversionId() {
  try {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  } catch (_) {
    /* fall through */
  }
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}
